import React, { useState } from 'react';
import { useAuth } from './AuthContext';

export function AuthModal({ isOpen, onClose }) {
  const { login, register } = useAuth();
  const [mode, setMode]         = useState('login'); // 'login' | 'register'
  const [username, setUsername] = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  if (!isOpen) return null;

  const reset = () => {
    setUsername(''); setEmail(''); setPassword(''); setError('');
  };

  const switchMode = (m) => { setMode(m); reset(); };

  const handleSubmit = async () => {
    setError('');
    if (!username || !password) { setError('Please fill in all fields'); return; }
    if (mode === 'register' && !email) { setError('Email is required'); return; }
    if (mode === 'register' && password.length < 6) { setError('Password must be at least 6 characters'); return; }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(username, password);
      } else {
        await register(username, email, password);
      }
      reset();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter') handleSubmit(); };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.title}>
            {mode === 'login' ? 'Welcome back' : 'Create account'}
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(mode === 'login' ? styles.tabActive : {}) }}
            onClick={() => switchMode('login')}
          >Login</button>
          <button
            style={{ ...styles.tab, ...(mode === 'register' ? styles.tabActive : {}) }}
            onClick={() => switchMode('register')}
          >Register</button>
        </div>

        {/* Form */}
        <div style={styles.body}>
          <div style={styles.field}>
            <label style={styles.label}>Username</label>
            <input
              style={styles.input}
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={handleKey}
              placeholder="your username"
              autoFocus
            />
          </div>

          {mode === 'register' && (
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                style={styles.input}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={handleKey}
                placeholder="you@example.com"
              />
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={handleKey}
              placeholder={mode === 'register' ? 'at least 6 characters' : '••••••••'}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button
            style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Create Account'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────
const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(26,26,46,0.6)',
    zIndex: 2000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    background: '#faf7f2',
    borderRadius: 8,
    width: 360,
    boxShadow: '0 8px 40px rgba(26,26,46,0.2)',
    overflow: 'hidden',
    fontFamily: "'DM Mono', monospace",
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 20px 14px',
    background: '#1a1a2e',
    borderBottom: '2px solid #c84b31',
  },
  title: {
    fontFamily: "'Fraunces', serif",
    fontSize: 17, fontWeight: 600,
    color: '#f5f0e8',
  },
  closeBtn: {
    background: 'none', border: 'none',
    color: '#8a8070', fontSize: 14,
    cursor: 'pointer', padding: 4,
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #d0c8b8',
  },
  tab: {
    flex: 1, padding: '10px',
    background: 'none', border: 'none',
    fontFamily: "'DM Mono', monospace",
    fontSize: 12, fontWeight: 500,
    color: '#8a8070', cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: -1,
  },
  tabActive: {
    color: '#1a1a2e',
    borderBottom: '2px solid #c84b31',
  },
  body: { padding: '20px' },
  field: { marginBottom: 14 },
  label: {
    display: 'block',
    fontSize: 10, fontWeight: 500,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    color: '#8a8070', marginBottom: 5,
  },
  input: {
    width: '100%', padding: '8px 10px',
    background: 'white', border: '1px solid #d0c8b8',
    borderRadius: 5,
    fontFamily: "'DM Mono', monospace",
    fontSize: 13, color: '#1a1a2e',
    outline: 'none', boxSizing: 'border-box',
  },
  error: {
    background: '#fde8e4', border: '1px solid #c84b31',
    borderRadius: 5, padding: '8px 12px',
    fontSize: 12, color: '#c84b31',
    marginBottom: 14,
  },
  submitBtn: {
    width: '100%', padding: '10px',
    background: '#1a1a2e', color: '#f5f0e8',
    border: 'none', borderRadius: 5,
    fontFamily: "'DM Mono', monospace",
    fontSize: 13, fontWeight: 500,
    cursor: 'pointer', marginTop: 4,
  },
};
